/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxChart from 'devextreme/viz/chart';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxoAdaptiveLayoutModule } from './nested/adaptive-layout';
import { DxoAnimationModule } from './nested/animation';
import { DxoArgumentAxisModule } from './nested/argument-axis';
import { DxiBreakModule } from './nested/break-dxi';
import { DxoBreakStyleModule } from './nested/break-style';
import { DxiConstantLineModule } from './nested/constant-line-dxi';
import { DxoLabelModule } from './nested/label';
import { DxoFontModule } from './nested/font';
import { DxoConstantLineStyleModule } from './nested/constant-line-style';
import { DxoGridModule } from './nested/grid';
import { DxoFormatModule } from './nested/format';
import { DxoOverlappingBehaviorModule } from './nested/overlapping-behavior';
import { DxoMinorGridModule } from './nested/minor-grid';
import { DxoMinorTickModule } from './nested/minor-tick';
import { DxoMinorTickIntervalModule } from './nested/minor-tick-interval';
import { DxiStripModule } from './nested/strip-dxi';
import { DxoStripStyleModule } from './nested/strip-style';
import { DxoTickModule } from './nested/tick';
import { DxoTickIntervalModule } from './nested/tick-interval';
import { DxoTitleModule } from './nested/title';
import { DxoCommonAxisSettingsModule } from './nested/common-axis-settings';
import { DxoCommonPaneSettingsModule } from './nested/common-pane-settings';
import { DxoBorderModule } from './nested/border';
import { DxoCommonSeriesSettingsModule } from './nested/common-series-settings';
import { DxoAreaModule } from './nested/area';
import { DxoHoverStyleModule } from './nested/hover-style';
import { DxoHatchingModule } from './nested/hatching';
import { DxoConnectorModule } from './nested/connector';
import { DxoPointModule } from './nested/point';
import { DxoImageModule } from './nested/image';
import { DxoHeightModule } from './nested/height';
import { DxoUrlModule } from './nested/url';
import { DxoWidthModule } from './nested/width';
import { DxoSelectionStyleModule } from './nested/selection-style';
import { DxoReductionModule } from './nested/reduction';
import { DxoValueErrorBarModule } from './nested/value-error-bar';
import { DxoBarModule } from './nested/bar';
import { DxoBubbleModule } from './nested/bubble';
import { DxoCandlestickModule } from './nested/candlestick';
import { DxoFullstackedareaModule } from './nested/fullstackedarea';
import { DxoFullstackedbarModule } from './nested/fullstackedbar';
import { DxoFullstackedlineModule } from './nested/fullstackedline';
import { DxoFullstackedsplineModule } from './nested/fullstackedspline';
import { DxoFullstackedsplineareaModule } from './nested/fullstackedsplinearea';
import { DxoArgumentFormatModule } from './nested/argument-format';
import { DxoLineModule } from './nested/line';
import { DxoRangeareaModule } from './nested/rangearea';
import { DxoRangebarModule } from './nested/rangebar';
import { DxoScatterModule } from './nested/scatter';
import { DxoSplineModule } from './nested/spline';
import { DxoSplineareaModule } from './nested/splinearea';
import { DxoStackedareaModule } from './nested/stackedarea';
import { DxoStackedbarModule } from './nested/stackedbar';
import { DxoStackedlineModule } from './nested/stackedline';
import { DxoStackedsplineModule } from './nested/stackedspline';
import { DxoStackedsplineareaModule } from './nested/stackedsplinearea';
import { DxoStepareaModule } from './nested/steparea';
import { DxoSteplineModule } from './nested/stepline';
import { DxoStockModule } from './nested/stock';
import { DxoCrosshairModule } from './nested/crosshair';
import { DxoHorizontalLineModule } from './nested/horizontal-line';
import { DxoVerticalLineModule } from './nested/vertical-line';
import { DxoDataPrepareSettingsModule } from './nested/data-prepare-settings';
import { DxoExportModule } from './nested/export';
import { DxoLegendModule } from './nested/legend';
import { DxoMarginModule } from './nested/margin';
import { DxoLoadingIndicatorModule } from './nested/loading-indicator';
import { DxiPaneModule } from './nested/pane-dxi';
import { DxoScrollBarModule } from './nested/scroll-bar';
import { DxiSeriesModule } from './nested/series-dxi';
import { DxoSeriesTemplateModule } from './nested/series-template';
import { DxoSizeModule } from './nested/size';
import { DxoSubtitleModule } from './nested/subtitle';
import { DxoTooltipModule } from './nested/tooltip';
import { DxoShadowModule } from './nested/shadow';
import { DxiValueAxisModule } from './nested/value-axis-dxi';

import { DxiPaneComponent } from './nested/pane-dxi';
import { DxiSeriesComponent } from './nested/series-dxi';
import { DxiValueAxisComponent } from './nested/value-axis-dxi';




/**
 * The Chart is a widget that visualizes data from a local or remote storage using a great variety of series types along with different interactive elements, such as tooltips, crosshair pointer, legend, etc.
 */

export class DxChartComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxChart;


    /**
     * Specifies adaptive layout options.
     */
    
    get adaptiveLayout(): any {
        return this._getOption('adaptiveLayout');
    }
    set adaptiveLayout(value: any) {
        this._setOption('adaptiveLayout', value);
    }


    /**
     * Specifies whether or not to adjust the value axis when zooming the widget.
     */
    
    get adjustOnZoom(): boolean {
        return this._getOption('adjustOnZoom');
    }
    set adjustOnZoom(value: boolean) {
        this._setOption('adjustOnZoom', value);
    }


    /**
     * Specifies animation options.
     */
    
    get animation(): boolean|
        { duration?: number, easing?: string, enabled?: boolean, maxPointCountSupported?: number } {
        return this._getOption('animation');
    }
    set animation(value: boolean|
        { duration?: number, easing?: string, enabled?: boolean, maxPointCountSupported?: number }) {
        this._setOption('animation', value);
    }


    /**
     * Configures the argument axis.
     */
    
    get argumentAxis(): any {
        return this._getOption('argumentAxis');
    }
    set argumentAxis(value: any) {
        this._setOption('argumentAxis', value);
    }


    /**
     * Controls the width of bars in the widget. Applies only to bar-like series and when the equalBarWidth option is true.
     */
    
    get barWidth(): number {
        return this._getOption('barWidth');
    }
    set barWidth(value: number) {
        this._setOption('barWidth', value);
    }


    /**
     * Defines common settings for both the argument and value axis in a chart.
     */
    
    get commonAxisSettings(): any {
        return this._getOption('commonAxisSettings');
    }
    set commonAxisSettings(value: any) {
        this._setOption('commonAxisSettings', value);
    }


    /**
     * Defines common settings for all panes in a chart.
     */
    
    get commonPaneSettings(): any {
        return this._getOption('commonPaneSettings');
    }
    set commonPaneSettings(value: any) {
        this._setOption('commonPaneSettings', value);
    }


    /**
     * Specifies settings common for all series in the chart.
     */
    
    get commonSeriesSettings(): any {
        return this._getOption('commonSeriesSettings');
    }
    set commonSeriesSettings(value: any) {
        this._setOption('commonSeriesSettings', value);
    }


    /**
     * Colors the background of the chart container.
     */
    
    get containerBackgroundColor(): string {
        return this._getOption('containerBackgroundColor');
    }
    set containerBackgroundColor(value: string) {
        this._setOption('containerBackgroundColor', value);
    }


    /**
     * Configures the crosshair feature.
     */
    
    get crosshair(): any {
        return this._getOption('crosshair');
    }
    set crosshair(value: any) {
        this._setOption('crosshair', value);
    }


    /**
     * Customizes the appearance of an individual point label.
     */
    
    get customizeLabel(): Function {
        return this._getOption('customizeLabel');
    }
    set customizeLabel(value: Function) {
        this._setOption('customizeLabel', value);
    }


    /**
     * Customizes the appearance of an individual series point.
     */
    
    get customizePoint(): Function {
        return this._getOption('customizePoint');
    }
    set customizePoint(value: Function) {
        this._setOption('customizePoint', value);
    }


    /**
     * Processes data before visualizing it.
     */
    
    get dataPrepareSettings(): any {
        return this._getOption('dataPrepareSettings');
    }
    set dataPrepareSettings(value: any) {
        this._setOption('dataPrepareSettings', value);
    }


    /**
     * Specifies the widget's data origin.
     */
    
    get dataSource(): DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<any> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<any>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies which pane should be used by default.
     */
    
    get defaultPane(): string {
        return this._getOption('defaultPane');
    }
    set defaultPane(value: string) {
        this._setOption('defaultPane', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether all bars should have equal width, even if some values are missing from a series. Applies only to bar-like series.
     */
    
    get equalBarWidth(): boolean {
        return this._getOption('equalBarWidth');
    }
    set equalBarWidth(value: boolean) {
        this._setOption('equalBarWidth', value);
    }


    /**
     * Configures the exporting and printing features.
     */
    
    get export(): any {
        return this._getOption('export');
    }
    set export(value: any) {
        this._setOption('export', value);
    }


    /**
     * Specifies the options of a chart's legend.
     */
    
    get legend(): any {
        return this._getOption('legend');
    }
    set legend(value: any) {
        this._setOption('legend', value);
    }


    /**
     * Configures the loading indicator.
     */
    
    get loadingIndicator(): any {
        return this._getOption('loadingIndicator');
    }
    set loadingIndicator(value: any) {
        this._setOption('loadingIndicator', value);
    }


    /**
     * Generates space around the widget.
     */
    
    get margin(): any {
        return this._getOption('margin');
    }
    set margin(value: any) {
        this._setOption('margin', value);
    }


    /**
     * Specifies a coefficient determining the diameter of the largest bubble.
     */
    
    get maxBubbleSize(): number {
        return this._getOption('maxBubbleSize');
    }
    set maxBubbleSize(value: number) {
        this._setOption('maxBubbleSize', value);
    }


    /**
     * Specifies the diameter of the smallest bubble measured in pixels.
     */
    
    get minBubbleSize(): number {
        return this._getOption('minBubbleSize');
    }
    set minBubbleSize(value: number) {
        this._setOption('minBubbleSize', value);
    }


    /**
     * Forces the widget to treat negative values as zeroes. Applies to stacked-like series only.
     */
    
    get negativesAsZeroes(): boolean {
        return this._getOption('negativesAsZeroes');
    }
    set negativesAsZeroes(value: boolean) {
        this._setOption('negativesAsZeroes', value);
    }


    /**
     * Sets the name of the palette to be used in the chart. Alternatively, an array of colors can be set as a custom palette to be used within this chart.
     */
    
    get palette(): string|
        Array<string> {
        return this._getOption('palette');
    }
    set palette(value: string|
        Array<string>) {
        this._setOption('palette', value);
    }


    /**
     * Declares a collection of panes.
     */
    
    get panes(): Array<any|
        { backgroundColor?: string, border?: { bottom?: boolean, color?: string, dashStyle?: string, left?: boolean, opacity?: number, right?: boolean, top?: boolean, visible?: boolean, width?: number }, name?: string }> {
        return this._getOption('panes');
    }
    set panes(value: Array<any|
        { backgroundColor?: string, border?: { bottom?: boolean, color?: string, dashStyle?: string, left?: boolean, opacity?: number, right?: boolean, top?: boolean, visible?: boolean, width?: number }, name?: string }>) {
        this._setOption('panes', value);
    }


    /**
     * Notifies the widget that it is embedded into an HTML page that uses a tag modifying the path.
     */
    
    get pathModified(): boolean {
        return this._getOption('pathModified');
    }
    set pathModified(value: boolean) {
        this._setOption('pathModified', value);
    }


    /**
     * Specifies whether a single point or multiple points can be selected in the chart.
     */
    
    get pointSelectionMode(): string {
        return this._getOption('pointSelectionMode');
    }
    set pointSelectionMode(value: string) {
        this._setOption('pointSelectionMode', value);
    }


    /**
     * Specifies whether to redraw the widget when the size of the parent browser window changes or a mobile device rotates.
     */
    
    get redrawOnResize(): boolean {
        return this._getOption('redrawOnResize');
    }
    set redrawOnResize(value: boolean) {
        this._setOption('redrawOnResize', value);
    }


    /**
     * Specifies how the chart must behave when series point labels overlap.
     */
    
    get resolveLabelOverlapping(): string {
        return this._getOption('resolveLabelOverlapping');
    }
    set resolveLabelOverlapping(value: string) {
        this._setOption('resolveLabelOverlapping', value);
    }


    /**
     * Swaps the axes around making the value axis horizontal and the argument axis vertical.
     */
    
    get rotated(): boolean {
        return this._getOption('rotated');
    }
    set rotated(value: boolean) {
        this._setOption('rotated', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies the settings of the scroll bar.
     */
    
    get scrollBar(): any {
        return this._getOption('scrollBar');
    }
    set scrollBar(value: any) {
        this._setOption('scrollBar', value);
    }


    /**
     * Enables scrolling in your chart.
     */
    
    get scrollingMode(): string {
        return this._getOption('scrollingMode');
    }
    set scrollingMode(value: string) {
        this._setOption('scrollingMode', value);
    }


    /**
     * Specifies options for Chart widget series.
     */
    
    get series(): any|
        Array<any> {
        return this._getOption('series');
    }
    set series(value: any|
        Array<any>) {
        this._setOption('series', value);
    }


    /**
     * Specifies whether a single series or multiple series can be selected in the chart.
     */
    
    get seriesSelectionMode(): string {
        return this._getOption('seriesSelectionMode');
    }
    set seriesSelectionMode(value: string) {
        this._setOption('seriesSelectionMode', value);
    }


    /**
     * Defines options for the series template.
     */
    
    get seriesTemplate(): any {
        return this._getOption('seriesTemplate');
    }
    set seriesTemplate(value: any) {
        this._setOption('seriesTemplate', value);
    }


    /**
     * Specifies the widget's size in pixels.
     */
    
    get size(): any {
        return this._getOption('size');
    }
    set size(value: any) {
        this._setOption('size', value);
    }


    /**
     * Indicates whether or not to synchronize value axes when they are displayed on a single pane.
     */
    
    get synchronizeMultiAxes(): boolean {
        return this._getOption('synchronizeMultiAxes');
    }
    set synchronizeMultiAxes(value: boolean) {
        this._setOption('synchronizeMultiAxes', value);
    }


    /**
     * Sets the name of the theme the widget uses.
     */
    
    get theme(): string {
        return this._getOption('theme');
    }
    set theme(value: string) {
        this._setOption('theme', value);
    }


    /**
     * Configures the widget's title.
     */
    
    get title(): string|
        { font?: DevExpress.viz.Font, horizontalAlignment?: string, margin?: number|
        { bottom?: number, left?: number, right?: number, top?: number }, placeholderSize?: number, subtitle?: string|
        { font?: DevExpress.viz.Font, text?: string }, text?: string, verticalAlignment?: string } {
        return this._getOption('title');
    }
    set title(value: string|
        { font?: DevExpress.viz.Font, horizontalAlignment?: string, margin?: number|
        { bottom?: number, left?: number, right?: number, top?: number }, placeholderSize?: number, subtitle?: string|
        { font?: DevExpress.viz.Font, text?: string }, text?: string, verticalAlignment?: string }) {
        this._setOption('title', value);
    }


    /**
     * Configures tooltips.
     */
    
    get tooltip(): any {
        return this._getOption('tooltip');
    }
    set tooltip(value: any) {
        this._setOption('tooltip', value);
    }


    /**
     * Specifies whether or not to filter the series points depending on their quantity.
     */
    
    get useAggregation(): boolean {
        return this._getOption('useAggregation');
    }
    set useAggregation(value: boolean) {
        this._setOption('useAggregation', value);
    }


    /**
     * Configures the value axis.
     */
    
    get valueAxis(): Array<any|
        { allowDecimals?: boolean, autoBreaksEnabled?: boolean, axisDivisionFactor?: number, breaks?: Array<DevExpress.viz.ScaleBreak|
        { endValue?: Date|
        number|
        string, startValue?: Date|
        number|
        string }>, breakStyle?: { color?: string, line?: string, width?: number }, categories?: Array<number|
        string|
        Date>, color?: string, constantLines?: Array<any|
        { color?: string, dashStyle?: string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, position?: string, text?: string, verticalAlignment?: string, visible?: boolean }, paddingLeftRight?: number, paddingTopBottom?: number, value?: Date|
        number|
        string, width?: number }>, constantLineStyle?: { color?: string, dashStyle?: string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, position?: string, verticalAlignment?: string, visible?: boolean }, paddingLeftRight?: number, paddingTopBottom?: number, width?: number }, discreteAxisDivisionMode?: string, endOnTick?: boolean, grid?: { color?: string, opacity?: number, visible?: boolean, width?: number }, inverted?: boolean, label?: { alignment?: string, customizeHint?: Function, customizeText?: Function, displayMode?: string, font?: DevExpress.viz.Font, format?: DevExpress.ui.format|
        string, indentFromAxis?: number, overlappingBehavior?: string|
        { mode?: string, rotationAngle?: number, staggeringSpacing?: number }, precision?: number, rotationAngle?: number, staggeringSpacing?: number, visible?: boolean }, logarithmBase?: number, max?: Date|
        number|
        string, maxAutoBreakCount?: number, maxValueMargin?: number, min?: Date|
        number|
        string, minorGrid?: { color?: string, opacity?: number, visible?: boolean, width?: number }, minorTick?: { color?: string, length?: number, opacity?: number, visible?: boolean, width?: number }, minorTickCount?: number, minorTickInterval?: number|
        string|
        { days?: number, hours?: number, milliseconds?: number, minutes?: number, months?: number, quarters?: number, seconds?: number, weeks?: number, years?: number }, minValueMargin?: number, multipleAxesSpacing?: number, name?: string, opacity?: number, pane?: string, placeholderSize?: number, position?: string, setTicksAtUnitBeginning?: boolean, showZero?: boolean, strips?: Array<any|
        { color?: string, endValue?: Date|
        number|
        string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, text?: string, verticalAlignment?: string }, paddingLeftRight?: number, paddingTopBottom?: number, startValue?: Date|
        number|
        string }>, stripStyle?: { label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, verticalAlignment?: string }, paddingLeftRight?: number, paddingTopBottom?: number }, synchronizedValue?: number, tick?: { color?: string, length?: number, opacity?: number, visible?: boolean, width?: number }, tickInterval?: number|
        string|
        { days?: number, hours?: number, milliseconds?: number, minutes?: number, months?: number, quarters?: number, seconds?: number, weeks?: number, years?: number }, title?: string|
        { font?: DevExpress.viz.Font, margin?: number, text?: string }, type?: string, valueMarginsEnabled?: boolean, valueType?: string, visible?: boolean, width?: number }> {
        return this._getOption('valueAxis');
    }
    set valueAxis(value: Array<any|
        { allowDecimals?: boolean, autoBreaksEnabled?: boolean, axisDivisionFactor?: number, breaks?: Array<DevExpress.viz.ScaleBreak|
        { endValue?: Date|
        number|
        string, startValue?: Date|
        number|
        string }>, breakStyle?: { color?: string, line?: string, width?: number }, categories?: Array<number|
        string|
        Date>, color?: string, constantLines?: Array<any|
        { color?: string, dashStyle?: string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, position?: string, text?: string, verticalAlignment?: string, visible?: boolean }, paddingLeftRight?: number, paddingTopBottom?: number, value?: Date|
        number|
        string, width?: number }>, constantLineStyle?: { color?: string, dashStyle?: string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, position?: string, verticalAlignment?: string, visible?: boolean }, paddingLeftRight?: number, paddingTopBottom?: number, width?: number }, discreteAxisDivisionMode?: string, endOnTick?: boolean, grid?: { color?: string, opacity?: number, visible?: boolean, width?: number }, inverted?: boolean, label?: { alignment?: string, customizeHint?: Function, customizeText?: Function, displayMode?: string, font?: DevExpress.viz.Font, format?: DevExpress.ui.format|
        string, indentFromAxis?: number, overlappingBehavior?: string|
        { mode?: string, rotationAngle?: number, staggeringSpacing?: number }, precision?: number, rotationAngle?: number, staggeringSpacing?: number, visible?: boolean }, logarithmBase?: number, max?: Date|
        number|
        string, maxAutoBreakCount?: number, maxValueMargin?: number, min?: Date|
        number|
        string, minorGrid?: { color?: string, opacity?: number, visible?: boolean, width?: number }, minorTick?: { color?: string, length?: number, opacity?: number, visible?: boolean, width?: number }, minorTickCount?: number, minorTickInterval?: number|
        string|
        { days?: number, hours?: number, milliseconds?: number, minutes?: number, months?: number, quarters?: number, seconds?: number, weeks?: number, years?: number }, minValueMargin?: number, multipleAxesSpacing?: number, name?: string, opacity?: number, pane?: string, placeholderSize?: number, position?: string, setTicksAtUnitBeginning?: boolean, showZero?: boolean, strips?: Array<any|
        { color?: string, endValue?: Date|
        number|
        string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, text?: string, verticalAlignment?: string }, paddingLeftRight?: number, paddingTopBottom?: number, startValue?: Date|
        number|
        string }>, stripStyle?: { label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, verticalAlignment?: string }, paddingLeftRight?: number, paddingTopBottom?: number }, synchronizedValue?: number, tick?: { color?: string, length?: number, opacity?: number, visible?: boolean, width?: number }, tickInterval?: number|
        string|
        { days?: number, hours?: number, milliseconds?: number, minutes?: number, months?: number, quarters?: number, seconds?: number, weeks?: number, years?: number }, title?: string|
        { font?: DevExpress.viz.Font, margin?: number, text?: string }, type?: string, valueMarginsEnabled?: boolean, valueType?: string, visible?: boolean, width?: number }>) {
        this._setOption('valueAxis', value);
    }


    /**
     * Enables zooming in your chart.
     */
    
    get zoomingMode(): string {
        return this._getOption('zoomingMode');
    }
    set zoomingMode(value: string) {
        this._setOption('zoomingMode', value);
    }

    /**
     * A handler for the argumentAxisClick event.
     */
     onArgumentAxisClick: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the done event.
     */
     onDone: EventEmitter<any>;

    /**
     * A handler for the drawn event. Executed when the widget has finished drawing itself.
     */
     onDrawn: EventEmitter<any>;

    /**
     * A handler for the exported event. Executed after data from the widget is exported.
     */
     onExported: EventEmitter<any>;

    /**
     * A handler for the exporting event. Executed before data from the widget is exported.
     */
     onExporting: EventEmitter<any>;

    /**
     * A handler for the fileSaving event. Executed before a file with exported data is saved on the user's local storage.
     */
     onFileSaving: EventEmitter<any>;

    /**
     * A handler for the incidentOccurred event. Executed when an error or warning appears in the widget.
     */
     onIncidentOccurred: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the legendClick event.
     */
     onLegendClick: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the pointClick event.
     */
     onPointClick: EventEmitter<any>;

    /**
     * A handler for the pointHoverChanged event.
     */
     onPointHoverChanged: EventEmitter<any>;

    /**
     * A handler for the pointSelectionChanged event.
     */
     onPointSelectionChanged: EventEmitter<any>;

    /**
     * A handler for the seriesClick event.
     */
     onSeriesClick: EventEmitter<any>;

    /**
     * A handler for the seriesHoverChanged event.
     */
     onSeriesHoverChanged: EventEmitter<any>;

    /**
     * A handler for the seriesSelectionChanged event.
     */
     onSeriesSelectionChanged: EventEmitter<any>;

    /**
     * A handler for the tooltipHidden event.
     */
     onTooltipHidden: EventEmitter<any>;

    /**
     * A handler for the tooltipShown event.
     */
     onTooltipShown: EventEmitter<any>;

    /**
     * A handler for the zoomEnd event.
     */
     onZoomEnd: EventEmitter<any>;

    /**
     * A handler for the zoomStart event.
     */
     onZoomStart: EventEmitter<any>;

    /**
     * A handler for the adaptiveLayoutChange event.
     */
     adaptiveLayoutChange: EventEmitter<any>;

    /**
     * A handler for the adjustOnZoomChange event.
     */
     adjustOnZoomChange: EventEmitter<boolean>;

    /**
     * A handler for the animationChange event.
     */
     animationChange: EventEmitter<boolean|
        { duration?: number, easing?: string, enabled?: boolean, maxPointCountSupported?: number }>;

    /**
     * A handler for the argumentAxisChange event.
     */
     argumentAxisChange: EventEmitter<any>;

    /**
     * A handler for the barWidthChange event.
     */
     barWidthChange: EventEmitter<number>;

    /**
     * A handler for the commonAxisSettingsChange event.
     */
     commonAxisSettingsChange: EventEmitter<any>;

    /**
     * A handler for the commonPaneSettingsChange event.
     */
     commonPaneSettingsChange: EventEmitter<any>;

    /**
     * A handler for the commonSeriesSettingsChange event.
     */
     commonSeriesSettingsChange: EventEmitter<any>;

    /**
     * A handler for the containerBackgroundColorChange event.
     */
     containerBackgroundColorChange: EventEmitter<string>;

    /**
     * A handler for the crosshairChange event.
     */
     crosshairChange: EventEmitter<any>;

    /**
     * A handler for the customizeLabelChange event.
     */
     customizeLabelChange: EventEmitter<Function>;

    /**
     * A handler for the customizePointChange event.
     */
     customizePointChange: EventEmitter<Function>;

    /**
     * A handler for the dataPrepareSettingsChange event.
     */
     dataPrepareSettingsChange: EventEmitter<any>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<any>>;

    /**
     * A handler for the defaultPaneChange event.
     */
     defaultPaneChange: EventEmitter<string>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the equalBarWidthChange event.
     */
     equalBarWidthChange: EventEmitter<boolean>;

    /**
     * A handler for the exportChange event.
     */
     exportChange: EventEmitter<any>;

    /**
     * A handler for the legendChange event.
     */
     legendChange: EventEmitter<any>;

    /**
     * A handler for the loadingIndicatorChange event.
     */
     loadingIndicatorChange: EventEmitter<any>;

    /**
     * A handler for the marginChange event.
     */
     marginChange: EventEmitter<any>;

    /**
     * A handler for the maxBubbleSizeChange event.
     */
     maxBubbleSizeChange: EventEmitter<number>;

    /**
     * A handler for the minBubbleSizeChange event.
     */
     minBubbleSizeChange: EventEmitter<number>;

    /**
     * A handler for the negativesAsZeroesChange event.
     */
     negativesAsZeroesChange: EventEmitter<boolean>;

    /**
     * A handler for the paletteChange event.
     */
     paletteChange: EventEmitter<string|
        Array<string>>;

    /**
     * A handler for the panesChange event.
     */
     panesChange: EventEmitter<Array<any|
        { backgroundColor?: string, border?: { bottom?: boolean, color?: string, dashStyle?: string, left?: boolean, opacity?: number, right?: boolean, top?: boolean, visible?: boolean, width?: number }, name?: string }>>;

    /**
     * A handler for the pathModifiedChange event.
     */
     pathModifiedChange: EventEmitter<boolean>;

    /**
     * A handler for the pointSelectionModeChange event.
     */
     pointSelectionModeChange: EventEmitter<string>;

    /**
     * A handler for the redrawOnResizeChange event.
     */
     redrawOnResizeChange: EventEmitter<boolean>;

    /**
     * A handler for the resolveLabelOverlappingChange event.
     */
     resolveLabelOverlappingChange: EventEmitter<string>;

    /**
     * A handler for the rotatedChange event.
     */
     rotatedChange: EventEmitter<boolean>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the scrollBarChange event.
     */
     scrollBarChange: EventEmitter<any>;

    /**
     * A handler for the scrollingModeChange event.
     */
     scrollingModeChange: EventEmitter<string>;

    /**
     * A handler for the seriesChange event.
     */
     seriesChange: EventEmitter<any|
        Array<any>>;

    /**
     * A handler for the seriesSelectionModeChange event.
     */
     seriesSelectionModeChange: EventEmitter<string>;

    /**
     * A handler for the seriesTemplateChange event.
     */
     seriesTemplateChange: EventEmitter<any>;

    /**
     * A handler for the sizeChange event.
     */
     sizeChange: EventEmitter<any>;

    /**
     * A handler for the synchronizeMultiAxesChange event.
     */
     synchronizeMultiAxesChange: EventEmitter<boolean>;

    /**
     * A handler for the themeChange event.
     */
     themeChange: EventEmitter<string>;

    /**
     * A handler for the titleChange event.
     */
     titleChange: EventEmitter<string|
        { font?: DevExpress.viz.Font, horizontalAlignment?: string, margin?: number|
        { bottom?: number, left?: number, right?: number, top?: number }, placeholderSize?: number, subtitle?: string|
        { font?: DevExpress.viz.Font, text?: string }, text?: string, verticalAlignment?: string }>;

    /**
     * A handler for the tooltipChange event.
     */
     tooltipChange: EventEmitter<any>;

    /**
     * A handler for the useAggregationChange event.
     */
     useAggregationChange: EventEmitter<boolean>;

    /**
     * A handler for the valueAxisChange event.
     */
     valueAxisChange: EventEmitter<Array<any|
        { allowDecimals?: boolean, autoBreaksEnabled?: boolean, axisDivisionFactor?: number, breaks?: Array<DevExpress.viz.ScaleBreak|
        { endValue?: Date|
        number|
        string, startValue?: Date|
        number|
        string }>, breakStyle?: { color?: string, line?: string, width?: number }, categories?: Array<number|
        string|
        Date>, color?: string, constantLines?: Array<any|
        { color?: string, dashStyle?: string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, position?: string, text?: string, verticalAlignment?: string, visible?: boolean }, paddingLeftRight?: number, paddingTopBottom?: number, value?: Date|
        number|
        string, width?: number }>, constantLineStyle?: { color?: string, dashStyle?: string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, position?: string, verticalAlignment?: string, visible?: boolean }, paddingLeftRight?: number, paddingTopBottom?: number, width?: number }, discreteAxisDivisionMode?: string, endOnTick?: boolean, grid?: { color?: string, opacity?: number, visible?: boolean, width?: number }, inverted?: boolean, label?: { alignment?: string, customizeHint?: Function, customizeText?: Function, displayMode?: string, font?: DevExpress.viz.Font, format?: DevExpress.ui.format|
        string, indentFromAxis?: number, overlappingBehavior?: string|
        { mode?: string, rotationAngle?: number, staggeringSpacing?: number }, precision?: number, rotationAngle?: number, staggeringSpacing?: number, visible?: boolean }, logarithmBase?: number, max?: Date|
        number|
        string, maxAutoBreakCount?: number, maxValueMargin?: number, min?: Date|
        number|
        string, minorGrid?: { color?: string, opacity?: number, visible?: boolean, width?: number }, minorTick?: { color?: string, length?: number, opacity?: number, visible?: boolean, width?: number }, minorTickCount?: number, minorTickInterval?: number|
        string|
        { days?: number, hours?: number, milliseconds?: number, minutes?: number, months?: number, quarters?: number, seconds?: number, weeks?: number, years?: number }, minValueMargin?: number, multipleAxesSpacing?: number, name?: string, opacity?: number, pane?: string, placeholderSize?: number, position?: string, setTicksAtUnitBeginning?: boolean, showZero?: boolean, strips?: Array<any|
        { color?: string, endValue?: Date|
        number|
        string, label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, text?: string, verticalAlignment?: string }, paddingLeftRight?: number, paddingTopBottom?: number, startValue?: Date|
        number|
        string }>, stripStyle?: { label?: { font?: DevExpress.viz.Font, horizontalAlignment?: string, verticalAlignment?: string }, paddingLeftRight?: number, paddingTopBottom?: number }, synchronizedValue?: number, tick?: { color?: string, length?: number, opacity?: number, visible?: boolean, width?: number }, tickInterval?: number|
        string|
        { days?: number, hours?: number, milliseconds?: number, minutes?: number, months?: number, quarters?: number, seconds?: number, weeks?: number, years?: number }, title?: string|
        { font?: DevExpress.viz.Font, margin?: number, text?: string }, type?: string, valueMarginsEnabled?: boolean, valueType?: string, visible?: boolean, width?: number }>>;

    /**
     * A handler for the zoomingModeChange event.
     */
     zoomingModeChange: EventEmitter<string>;




    
    get panesChildren(): QueryList<DxiPaneComponent> {
        return this._getOption('panes');
    }
    set panesChildren(value) {
        this.setChildren('panes', value);
    }

    
    get seriesChildren(): QueryList<DxiSeriesComponent> {
        return this._getOption('series');
    }
    set seriesChildren(value) {
        this.setChildren('series', value);
    }

    
    get valueAxisChildren(): QueryList<DxiValueAxisComponent> {
        return this._getOption('valueAxis');
    }
    set valueAxisChildren(value) {
        this.setChildren('valueAxis', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'argumentAxisClick', emit: 'onArgumentAxisClick' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'done', emit: 'onDone' },
            { subscribe: 'drawn', emit: 'onDrawn' },
            { subscribe: 'exported', emit: 'onExported' },
            { subscribe: 'exporting', emit: 'onExporting' },
            { subscribe: 'fileSaving', emit: 'onFileSaving' },
            { subscribe: 'incidentOccurred', emit: 'onIncidentOccurred' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'legendClick', emit: 'onLegendClick' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'pointClick', emit: 'onPointClick' },
            { subscribe: 'pointHoverChanged', emit: 'onPointHoverChanged' },
            { subscribe: 'pointSelectionChanged', emit: 'onPointSelectionChanged' },
            { subscribe: 'seriesClick', emit: 'onSeriesClick' },
            { subscribe: 'seriesHoverChanged', emit: 'onSeriesHoverChanged' },
            { subscribe: 'seriesSelectionChanged', emit: 'onSeriesSelectionChanged' },
            { subscribe: 'tooltipHidden', emit: 'onTooltipHidden' },
            { subscribe: 'tooltipShown', emit: 'onTooltipShown' },
            { subscribe: 'zoomEnd', emit: 'onZoomEnd' },
            { subscribe: 'zoomStart', emit: 'onZoomStart' },
            { emit: 'adaptiveLayoutChange' },
            { emit: 'adjustOnZoomChange' },
            { emit: 'animationChange' },
            { emit: 'argumentAxisChange' },
            { emit: 'barWidthChange' },
            { emit: 'commonAxisSettingsChange' },
            { emit: 'commonPaneSettingsChange' },
            { emit: 'commonSeriesSettingsChange' },
            { emit: 'containerBackgroundColorChange' },
            { emit: 'crosshairChange' },
            { emit: 'customizeLabelChange' },
            { emit: 'customizePointChange' },
            { emit: 'dataPrepareSettingsChange' },
            { emit: 'dataSourceChange' },
            { emit: 'defaultPaneChange' },
            { emit: 'elementAttrChange' },
            { emit: 'equalBarWidthChange' },
            { emit: 'exportChange' },
            { emit: 'legendChange' },
            { emit: 'loadingIndicatorChange' },
            { emit: 'marginChange' },
            { emit: 'maxBubbleSizeChange' },
            { emit: 'minBubbleSizeChange' },
            { emit: 'negativesAsZeroesChange' },
            { emit: 'paletteChange' },
            { emit: 'panesChange' },
            { emit: 'pathModifiedChange' },
            { emit: 'pointSelectionModeChange' },
            { emit: 'redrawOnResizeChange' },
            { emit: 'resolveLabelOverlappingChange' },
            { emit: 'rotatedChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollBarChange' },
            { emit: 'scrollingModeChange' },
            { emit: 'seriesChange' },
            { emit: 'seriesSelectionModeChange' },
            { emit: 'seriesTemplateChange' },
            { emit: 'sizeChange' },
            { emit: 'synchronizeMultiAxesChange' },
            { emit: 'themeChange' },
            { emit: 'titleChange' },
            { emit: 'tooltipChange' },
            { emit: 'useAggregationChange' },
            { emit: 'valueAxisChange' },
            { emit: 'zoomingModeChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxChart(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('palette', changes);
        this.setupChanges('panes', changes);
        this.setupChanges('series', changes);
        this.setupChanges('valueAxis', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('palette');
        this._idh.doCheck('panes');
        this._idh.doCheck('series');
        this._idh.doCheck('valueAxis');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-chart',
    template: '',
    styles: [ ' :host {  display: block; }'],
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'adaptiveLayout': [{ type: Input },],
'adjustOnZoom': [{ type: Input },],
'animation': [{ type: Input },],
'argumentAxis': [{ type: Input },],
'barWidth': [{ type: Input },],
'commonAxisSettings': [{ type: Input },],
'commonPaneSettings': [{ type: Input },],
'commonSeriesSettings': [{ type: Input },],
'containerBackgroundColor': [{ type: Input },],
'crosshair': [{ type: Input },],
'customizeLabel': [{ type: Input },],
'customizePoint': [{ type: Input },],
'dataPrepareSettings': [{ type: Input },],
'dataSource': [{ type: Input },],
'defaultPane': [{ type: Input },],
'elementAttr': [{ type: Input },],
'equalBarWidth': [{ type: Input },],
'export': [{ type: Input },],
'legend': [{ type: Input },],
'loadingIndicator': [{ type: Input },],
'margin': [{ type: Input },],
'maxBubbleSize': [{ type: Input },],
'minBubbleSize': [{ type: Input },],
'negativesAsZeroes': [{ type: Input },],
'palette': [{ type: Input },],
'panes': [{ type: Input },],
'pathModified': [{ type: Input },],
'pointSelectionMode': [{ type: Input },],
'redrawOnResize': [{ type: Input },],
'resolveLabelOverlapping': [{ type: Input },],
'rotated': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'scrollBar': [{ type: Input },],
'scrollingMode': [{ type: Input },],
'series': [{ type: Input },],
'seriesSelectionMode': [{ type: Input },],
'seriesTemplate': [{ type: Input },],
'size': [{ type: Input },],
'synchronizeMultiAxes': [{ type: Input },],
'theme': [{ type: Input },],
'title': [{ type: Input },],
'tooltip': [{ type: Input },],
'useAggregation': [{ type: Input },],
'valueAxis': [{ type: Input },],
'zoomingMode': [{ type: Input },],
'onArgumentAxisClick': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onDone': [{ type: Output },],
'onDrawn': [{ type: Output },],
'onExported': [{ type: Output },],
'onExporting': [{ type: Output },],
'onFileSaving': [{ type: Output },],
'onIncidentOccurred': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onLegendClick': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onPointClick': [{ type: Output },],
'onPointHoverChanged': [{ type: Output },],
'onPointSelectionChanged': [{ type: Output },],
'onSeriesClick': [{ type: Output },],
'onSeriesHoverChanged': [{ type: Output },],
'onSeriesSelectionChanged': [{ type: Output },],
'onTooltipHidden': [{ type: Output },],
'onTooltipShown': [{ type: Output },],
'onZoomEnd': [{ type: Output },],
'onZoomStart': [{ type: Output },],
'adaptiveLayoutChange': [{ type: Output },],
'adjustOnZoomChange': [{ type: Output },],
'animationChange': [{ type: Output },],
'argumentAxisChange': [{ type: Output },],
'barWidthChange': [{ type: Output },],
'commonAxisSettingsChange': [{ type: Output },],
'commonPaneSettingsChange': [{ type: Output },],
'commonSeriesSettingsChange': [{ type: Output },],
'containerBackgroundColorChange': [{ type: Output },],
'crosshairChange': [{ type: Output },],
'customizeLabelChange': [{ type: Output },],
'customizePointChange': [{ type: Output },],
'dataPrepareSettingsChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'defaultPaneChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'equalBarWidthChange': [{ type: Output },],
'exportChange': [{ type: Output },],
'legendChange': [{ type: Output },],
'loadingIndicatorChange': [{ type: Output },],
'marginChange': [{ type: Output },],
'maxBubbleSizeChange': [{ type: Output },],
'minBubbleSizeChange': [{ type: Output },],
'negativesAsZeroesChange': [{ type: Output },],
'paletteChange': [{ type: Output },],
'panesChange': [{ type: Output },],
'pathModifiedChange': [{ type: Output },],
'pointSelectionModeChange': [{ type: Output },],
'redrawOnResizeChange': [{ type: Output },],
'resolveLabelOverlappingChange': [{ type: Output },],
'rotatedChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'scrollBarChange': [{ type: Output },],
'scrollingModeChange': [{ type: Output },],
'seriesChange': [{ type: Output },],
'seriesSelectionModeChange': [{ type: Output },],
'seriesTemplateChange': [{ type: Output },],
'sizeChange': [{ type: Output },],
'synchronizeMultiAxesChange': [{ type: Output },],
'themeChange': [{ type: Output },],
'titleChange': [{ type: Output },],
'tooltipChange': [{ type: Output },],
'useAggregationChange': [{ type: Output },],
'valueAxisChange': [{ type: Output },],
'zoomingModeChange': [{ type: Output },],
'panesChildren': [{ type: ContentChildren, args: [DxiPaneComponent, ] },],
'seriesChildren': [{ type: ContentChildren, args: [DxiSeriesComponent, ] },],
'valueAxisChildren': [{ type: ContentChildren, args: [DxiValueAxisComponent, ] },],
};
}


export class DxChartModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxoAdaptiveLayoutModule,
    DxoAnimationModule,
    DxoArgumentAxisModule,
    DxiBreakModule,
    DxoBreakStyleModule,
    DxiConstantLineModule,
    DxoLabelModule,
    DxoFontModule,
    DxoConstantLineStyleModule,
    DxoGridModule,
    DxoFormatModule,
    DxoOverlappingBehaviorModule,
    DxoMinorGridModule,
    DxoMinorTickModule,
    DxoMinorTickIntervalModule,
    DxiStripModule,
    DxoStripStyleModule,
    DxoTickModule,
    DxoTickIntervalModule,
    DxoTitleModule,
    DxoCommonAxisSettingsModule,
    DxoCommonPaneSettingsModule,
    DxoBorderModule,
    DxoCommonSeriesSettingsModule,
    DxoAreaModule,
    DxoHoverStyleModule,
    DxoHatchingModule,
    DxoConnectorModule,
    DxoPointModule,
    DxoImageModule,
    DxoHeightModule,
    DxoUrlModule,
    DxoWidthModule,
    DxoSelectionStyleModule,
    DxoReductionModule,
    DxoValueErrorBarModule,
    DxoBarModule,
    DxoBubbleModule,
    DxoCandlestickModule,
    DxoFullstackedareaModule,
    DxoFullstackedbarModule,
    DxoFullstackedlineModule,
    DxoFullstackedsplineModule,
    DxoFullstackedsplineareaModule,
    DxoArgumentFormatModule,
    DxoLineModule,
    DxoRangeareaModule,
    DxoRangebarModule,
    DxoScatterModule,
    DxoSplineModule,
    DxoSplineareaModule,
    DxoStackedareaModule,
    DxoStackedbarModule,
    DxoStackedlineModule,
    DxoStackedsplineModule,
    DxoStackedsplineareaModule,
    DxoStepareaModule,
    DxoSteplineModule,
    DxoStockModule,
    DxoCrosshairModule,
    DxoHorizontalLineModule,
    DxoVerticalLineModule,
    DxoDataPrepareSettingsModule,
    DxoExportModule,
    DxoLegendModule,
    DxoMarginModule,
    DxoLoadingIndicatorModule,
    DxiPaneModule,
    DxoScrollBarModule,
    DxiSeriesModule,
    DxoSeriesTemplateModule,
    DxoSizeModule,
    DxoSubtitleModule,
    DxoTooltipModule,
    DxoShadowModule,
    DxiValueAxisModule,
    DxTemplateModule
  ],
  declarations: [
    DxChartComponent
  ],
  exports: [
    DxChartComponent,
    DxoAdaptiveLayoutModule,
    DxoAnimationModule,
    DxoArgumentAxisModule,
    DxiBreakModule,
    DxoBreakStyleModule,
    DxiConstantLineModule,
    DxoLabelModule,
    DxoFontModule,
    DxoConstantLineStyleModule,
    DxoGridModule,
    DxoFormatModule,
    DxoOverlappingBehaviorModule,
    DxoMinorGridModule,
    DxoMinorTickModule,
    DxoMinorTickIntervalModule,
    DxiStripModule,
    DxoStripStyleModule,
    DxoTickModule,
    DxoTickIntervalModule,
    DxoTitleModule,
    DxoCommonAxisSettingsModule,
    DxoCommonPaneSettingsModule,
    DxoBorderModule,
    DxoCommonSeriesSettingsModule,
    DxoAreaModule,
    DxoHoverStyleModule,
    DxoHatchingModule,
    DxoConnectorModule,
    DxoPointModule,
    DxoImageModule,
    DxoHeightModule,
    DxoUrlModule,
    DxoWidthModule,
    DxoSelectionStyleModule,
    DxoReductionModule,
    DxoValueErrorBarModule,
    DxoBarModule,
    DxoBubbleModule,
    DxoCandlestickModule,
    DxoFullstackedareaModule,
    DxoFullstackedbarModule,
    DxoFullstackedlineModule,
    DxoFullstackedsplineModule,
    DxoFullstackedsplineareaModule,
    DxoArgumentFormatModule,
    DxoLineModule,
    DxoRangeareaModule,
    DxoRangebarModule,
    DxoScatterModule,
    DxoSplineModule,
    DxoSplineareaModule,
    DxoStackedareaModule,
    DxoStackedbarModule,
    DxoStackedlineModule,
    DxoStackedsplineModule,
    DxoStackedsplineareaModule,
    DxoStepareaModule,
    DxoSteplineModule,
    DxoStockModule,
    DxoCrosshairModule,
    DxoHorizontalLineModule,
    DxoVerticalLineModule,
    DxoDataPrepareSettingsModule,
    DxoExportModule,
    DxoLegendModule,
    DxoMarginModule,
    DxoLoadingIndicatorModule,
    DxiPaneModule,
    DxoScrollBarModule,
    DxiSeriesModule,
    DxoSeriesTemplateModule,
    DxoSizeModule,
    DxoSubtitleModule,
    DxoTooltipModule,
    DxoShadowModule,
    DxiValueAxisModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
